import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AppProvider } from "./context/AppState";
import { BrowserRouter } from "react-router-dom";

// Import Swiper styles
import swiperBaseStyles from "swiper/css";
import swiperNavigationStyles from "swiper/css/navigation";
import swiperPaginationStyles from "swiper/css/pagination";

export let shadowRootElement;
export let shadowRootElemenTag;

const injectLinksArray = [
  { rel: "stylesheet", href: "https://unpkg.com/swiper/swiper-bundle.min.css" },
]

const fontFamilies = [
  {
    prefix: "Aimbrill-Poppins",
    url: "https://fonts.gstatic.com/s/poppins/v22/pxiEyp8kv8JHgFVrJJfecg.woff2",
    format: "woff2",
  },
  {
    prefix: "Aimbrill-Domine",
    url: "https://fonts.gstatic.com/s/domine/v25/L0xhDFMnlVwD4h3Lt9JWnbX3jG-2X3LAI10.ttf",
    format: "truetype",
  },
  {
    prefix: "Aimbrill-Inter",
    url: "https://fonts.gstatic.com/s/inter/v20/UcCO3FwrK3iLTeHuS_nVMrMxCp50SjIw2boKoduKmMEVuLyfMZg.ttf",
    format: "truetype",
  },
];

const injectStylesArray = [
  swiperBaseStyles,
  swiperNavigationStyles,
  swiperPaginationStyles,
]


function injectStylesAndLinksIntoHead() {
  // Links
  for (const link of injectLinksArray) {
    const linkElement = document.createElement("link");
    linkElement.rel = link.rel;
    linkElement.href = link.href;
    document.head.appendChild(linkElement);
  }

  // Styles
  const headStyleElement = document.createElement("style");
  for (const style of injectStylesArray) {
    headStyleElement.textContent += `${JSON.stringify(style)}`;
  }
  document.head.appendChild(headStyleElement);

  // Fonts
  const styleElement = document.createElement("style");
  styleElement.className = "font-styleElement";

  let fontStyles = "";

  for (const font of fontFamilies) {
    fontStyles += `
        @font-face {
            font-family: '${font.prefix}';
            font-style: normal;
            font-weight: 400;
            src: url('${font.url}') format('${font.format}'); 
          }
    `;
  }

  styleElement.textContent = fontStyles;
  document.head.appendChild(styleElement);

  // const HeadFontStyle = document.createElement("style");
  // HeadFontStyle.textContent = `
  //   * {
  //     font-family: 'Aimbrill-Poppins', 'Aimbrill-Domine', 'Aimbrill-Inter', sans-serif;
  //   }
  // `;
  // document.head.appendChild(HeadFontStyle);
}


function injectStylesAndLinksIntoShadowRoot(shadowRoot) {
  // Links
  for (const link of injectLinksArray) {
    const linkElement = document.createElement("link");
    linkElement.rel = link.rel;
    linkElement.href = link.href;
    shadowRoot.appendChild(linkElement);
  }

  // Styles
  const headStyleElement = document.createElement("style");
  for (const style of injectStylesArray) {
    headStyleElement.textContent += `${JSON.stringify(style)}`;
  }
  shadowRoot.appendChild(headStyleElement);

  // Fonts
  const styleElement = document.createElement("style");
  styleElement.className = "font-styleElement";
  let fontStyles = "";

  for (const font of fontFamilies) {
    fontStyles += `
        @font-face {
            font-display: swap; 
            font-family: '${font.prefix}';
            font-style: normal;
            font-weight: 400;
            src: url('${font.url}') format('${font.format}'); 
          }
    `;
  }

  styleElement.textContent = fontStyles;
  shadowRoot.appendChild(styleElement);


  const shadowFontStyle = document.createElement("style");
  shadowFontStyle.textContent = `
    #popup-preview-root {
      font-family: 'Aimbrill-Poppins', 'Aimbrill-Domine', 'Aimbrill-Inter', sans-serif;
    }
  `;
  shadowRoot.appendChild(shadowFontStyle);

}

function injectStylesAndLinks(shadowRoot) {
  injectStylesAndLinksIntoHead()
  injectStylesAndLinksIntoShadowRoot(shadowRoot)
}

function initializeApp(shadowRootContainer) {
  let shadowRoot;
  let reactRootElement;

  if (window.isLiveModeTrigger) {
    shadowRoot = shadowRootContainer.shadowRoot;

    if (!shadowRoot) {
      shadowRoot = shadowRootContainer.attachShadow({ mode: "open" });
    }

    reactRootElement = shadowRoot.querySelector("#popup-preview-root");
    if (!reactRootElement) {
      reactRootElement = document.createElement("div");
      reactRootElement.id = "popup-preview-root";
      shadowRoot.appendChild(reactRootElement);
    }
    injectStylesAndLinks(shadowRoot);
  } else {
    reactRootElement = document.querySelector("#popup-preview-root");
    if (!reactRootElement) {
      reactRootElement = document.createElement("div");
      reactRootElement.id = "popup-preview-root";
      document.body.appendChild(reactRootElement);
    }
    injectStylesAndLinks(document.body);
  }

  if (reactRootElement) {
    const shadowReactRoot = ReactDOM.createRoot(reactRootElement);
    shadowRootElement = shadowRoot || document.body;
    shadowRootElemenTag = reactRootElement;

    shadowReactRoot.render(
      <React.StrictMode>
        <BrowserRouter>
          <AppProvider>
            <App />
          </AppProvider>
        </BrowserRouter>
      </React.StrictMode>
    );
  }
}

function prepareShadowHost(shadowRootContainer) {
  shadowRootContainer.style.zIndex = "2147483647";
  shadowRootContainer.style.setProperty("display", "block", "important");
}

function startObserver() {
  const shadowRootContainer = document.querySelector("#popup-preview-shadow-root");
  if (shadowRootContainer) {
    console.log("#popup-preview-shadow-root already exists");
    prepareShadowHost(shadowRootContainer);
    initializeApp(shadowRootContainer);
    return;
  }

  const observer = new MutationObserver((mutationsList) => {
    console.log("Observer triggered");

    for (const mutation of mutationsList) {
      if (mutation.type === "childList") {
        console.log("Mutation detected:", mutation);
        const latestShadowRootContainer = document.querySelector("#popup-preview-shadow-root");

        if (latestShadowRootContainer) {
          console.log("#popup-preview-shadow-root found");
          observer.disconnect();
          prepareShadowHost(latestShadowRootContainer);
          initializeApp(latestShadowRootContainer);
          break;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

startObserver();

reportWebVitals();
